import {
	FormItem,
	TagSelect,
	ValidationError
} from "client/Components";
import { TagKind, TagSaveModel } from "client/Api/Tags/tags";
import { IValueInputAttachable } from "client/Forms/attachable";
import { AttachTagSelectOptions } from "client/Forms/attachableProps/tagSelect";
import { AlertManager, useAlerts } from "client/Hooks";
import { useSuggestedTagsDataSource } from "client/Features/Tags/useSuggestedTagsDataSource";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";

export const MAX_TAG_COUNT: number = 10;
export const MAX_TAG_LENGTH: number = 30;
export const MAX_SUGGESTED_TAG_COUNT: number = 20;

export type UserTagSelectProps = {
	label: string;
	attachable: IValueInputAttachable<readonly TagSaveModel[]>;
	kind: TagKind;
	placeholder?: string;
};

export const UserTagSelect = (props: UserTagSelectProps): ReactElement => {
	const {
		label,
		attachable,
		kind,
		placeholder
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [shouldLoadTags, setShouldLoadTags] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const tagSelectErrorHandler = useCallback(
		(message: string): void => {
			alertManager.showError(message);
		},
		[alertManager]
	);

	const tagSelectOptions = useMemo(
		(): AttachTagSelectOptions => {
			return {
				onError: tagSelectErrorHandler,
				maxTagCount: MAX_TAG_COUNT,
				maxTagLength: MAX_TAG_LENGTH,
			};
		},
		[tagSelectErrorHandler]
	);

	const {
		currentItems: suggestedTags,
		getLoadingState
	} = useSuggestedTagsDataSource({
		filtering: {
			selectedTagIds: attachable.value
				.map(x => x.id),
			count: MAX_SUGGESTED_TAG_COUNT,
			kind,
			shouldLoadData: shouldLoadTags
		},
		searchTerm: searchTerm
	});

	return (
		<FormItem label={label}>
			<TagSelect
				{...attachable.attachToTagSelect<number>(tagSelectOptions)}
				onSearch={setSearchTerm}
				suggestedTags={suggestedTags}
				searchDebounceInterval={250}
				onIsSuggestionsBoxOpenChange={setShouldLoadTags}
				isBusy={getLoadingState() === "loading"}
				menuTitle={label}
				placeholder={placeholder}
			/>
			<ValidationError {...attachable.attachToValidationError()} />
		</FormItem>
	);
};
UserTagSelect.displayName = "UserTagSelect";